<template>
  <SectionContainer :colors="colors" :background-variant="backgroundVariant" class="w-full"
  >
    <!-- Content -->
    <div
        v-motion
        :initial="{ opacity: 0, y: 50 }"
        :visible="{ opacity: 1, y: 0 }"
        :transition="{ duration: 1.5, ease: 'easeOut' }"
        class="relative z-10 w-full px-5 md:px-10 py-10 items-center justify-center flex flex-col"
    >
      <component :is="headingTag" class="text-center text-3xl md:text-5xl font-bold text-[var(--text-primary)]">
        {{ title }}
      </component>
      <p class="mt-10 text-center text-lg md:text-xl text-[var(--text-secondary)]">
        {{ content }}
      </p>
      <slot></slot>
      <div class="flex flex-col items-center justify-center">
        <WebButton v-if="callToAction" class="mt-6" @clicked="callToActionClicked">
          {{ callToAction }}
        </WebButton>
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { buttonAction } from "@/utils/buttonTarget.js";
import WebButton from "@/components/WebButton.vue";
import SectionContainer from "@/components/SectionContainer.vue";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const router = useRouter();

const props = defineProps({
  title: { type: String, required: true },
  content: { type: String, required: true },
  callToAction: { type: String, default: "" },
  scrollTarget: { type: String, default: "" }, // For scrolling behavior
  navigateTo: { type: String, default: "/contact" }, // For navigation behavior
  backgroundVariant: { type: Number, default: 1 }, // Alternating backgrounds
  colors: { type: Object, required: true } // Theme colors
});

// CSS variables for theming
const cssVars = computed(() => ({
  "--bg-light-variant-1": "#ffffff",
  "--bg-light-variant-2": props.colors.lightest,
  "--bg-dark-variant-1": props.colors.dark,
  "--bg-dark-variant-2": props.colors.darkest,
  "--text-primary": props.colors.textPrimary || "#333",
  "--text-secondary": props.colors.textSecondary || "#666"
}));

// Dynamically compute background class
const computedBackgroundClass = computed(() =>
    props.backgroundVariant === 1
        ? "bg-[var(--bg-light-variant-1)] dark2:bg-[var(--bg-dark-variant-1)]"
        : "bg-[var(--bg-light-variant-2)] dark2:bg-[var(--bg-dark-variant-2)]"
);

// Store navbar height reactively
const navHeight = ref(0);

onMounted(() => {
  const navbar = document.querySelector("nav");
  if (navbar) {
    navHeight.value = navbar.offsetHeight;
  }
});

const callToActionClicked = () => {
  const action = buttonAction({scrollTarget: props.scrollTarget, navigateTo: props.navigateTo});
  if (!action) return;
  if (action.kind === "scroll") {
    const targetElement = document.getElementById(action.id);
    if (targetElement) {
      const sectionTop = targetElement.offsetTop - navHeight.value;
      window.scrollTo({
        top: sectionTop,
        behavior: "smooth"
      });
    }
  } else if (action.kind === "external") {
    // tel:, mailto: ("Call us", "Email us") and web addresses.
    window.location.href = action.href;
  } else {
    router.push(back(action.path));
  }
};
</script>

<style scoped>
/* Smooth transition for background */
.transition-colors {
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
}
</style>
